import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';

import { formatCurrency } from '../charts/d3/format';
import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatMonthKeyLabel } from '../../hooks/useSpendBreakdown';
import {
  listTransactions,
  patchTransaction,
  type ApiTransaction,
} from '../../services/transactionService';
import {
  isOwnTransaction,
  resolveTransactionOwnerLabel,
} from '../../utils/transactionOwnership';
import {
  EditTransactionModal,
  type TransactionEditDraft,
  type TransactionEditTarget,
} from './EditTransactionModal';
import {
  PersonalOnlyChip,
  TransactionEditGlyph,
} from './TransactionRowChrome';
import type { SpendCategoryModalTarget } from './spendCategoryModal';

export type { SpendCategoryModalTarget };

type Props = {
  target: SpendCategoryModalTarget | null;
  onClose: () => void;
};

type Row = {
  id: string;
  date: string;
  merchant: string;
  amount: number;
  direction: 'debit' | 'credit';
  note?: string;
  categoryName?: string;
  userId?: string;
  hiddenFromHousehold?: boolean;
  ownerLabel?: string | null;
  canEdit: boolean;
};

function formatPeriodLabel(period: SpendCategoryModalTarget['period'], periodKey: string): string {
  if (period === 'year') {
    return /^\d{4}$/.test(periodKey) ? periodKey : `YTD ${periodKey}`;
  }
  if (/^\d{4}-\d{2}$/.test(periodKey)) return formatMonthKeyLabel(periodKey);
  return periodKey;
}

function formatShortDate(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Centered web dialog — DOM scroll so the transaction list scrolls reliably. */
export function SpendCategoryTransactionsModal({ target, onClose }: Props) {
  const { height, width } = useWindowDimensions();
  const { tokens, user } = useAuth();
  const {
    view: householdView,
    household,
    memberCount,
  } = useHouseholdMembership();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<TransactionEditTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const visible = target != null;
  const panelMaxHeight = Math.min(height * 0.82, 640);
  const listMaxHeight = Math.max(180, panelMaxHeight - 168);
  const usingSample = Boolean(target?.sampleTransactions);

  const mapTx = useCallback(
    (tx: ApiTransaction): Row => {
      const dateRaw = tx.date as unknown;
      const dateStr =
        typeof dateRaw === 'string'
          ? dateRaw
          : dateRaw instanceof Date
            ? dateRaw.toISOString()
            : String(dateRaw);
      const canEdit = isOwnTransaction(tx.userId, user?.id);
      return {
        id: tx.id,
        date: dateStr.slice(0, 10),
        merchant: tx.merchant,
        amount: Math.abs(tx.amount),
        direction: tx.direction,
        note: tx.description || undefined,
        categoryName: tx.categoryName,
        userId: tx.userId,
        hiddenFromHousehold: tx.hiddenFromHousehold === true,
        ownerLabel: resolveTransactionOwnerLabel({
          userId: tx.userId,
          view: householdView,
          members: household?.members ?? [],
          memberCount,
          viewerUserId: user?.id,
        }),
        canEdit,
      };
    },
    [household?.members, householdView, memberCount, user?.id],
  );

  useEffect(() => {
    if (!target) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (target.sampleTransactions) {
      setRows(
        [...target.sampleTransactions]
          .map((tx) => ({
            id: tx.id,
            date: tx.date.slice(0, 10),
            merchant: tx.merchant,
            amount: Math.abs(tx.amount),
            direction: tx.direction ?? 'debit',
            note: tx.note,
            canEdit: false as const,
          }))
          .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
      );
      setError(null);
      setLoading(false);
      return;
    }

    if (!tokens?.accessToken) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    void listTransactions({
      accessToken: tokens.accessToken,
      tab: 'spend',
      period: target.period,
      periodKey: target.periodKey,
      categorySlug: target.categorySlug,
      spendAccountId: target.spendAccountId,
      view: householdView,
      limit: 100,
    })
      .then((response) => {
        if (cancelled) return;
        setRows(response.transactions.map(mapTx));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [target, tokens?.accessToken, householdView, mapTx, reloadKey]);

  const handleSaveEdit = (draft: TransactionEditDraft) => {
    if (!editTarget) return;
    setSaving(true);
    void patchTransaction(editTarget.id, {
      categorySlug: draft.categorySlug,
      categoryName: draft.categoryName,
      hiddenFromHousehold: draft.hiddenFromHousehold,
    })
      .then(() => {
        setEditTarget(null);
        setReloadKey((k) => k + 1);
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss category transactions"
        />

        <View
          style={[
            styles.panelWrap,
            {
              maxWidth: Math.min(520, width - spacing.lg * 2),
              maxHeight: panelMaxHeight,
            },
          ]}
          pointerEvents="box-none"
        >
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={[styles.panel, { maxHeight: panelMaxHeight }]}
            contentStyle={styles.panelContent}
          >
            <View style={styles.header}>
              <View style={styles.headerText}>
                <Text style={styles.eyebrow}>Category</Text>
                <Text style={styles.title}>{target?.categoryName ?? ''}</Text>
                <Text style={styles.sub}>
                  {target
                    ? `${formatCurrency(target.amount)} · ${formatPeriodLabel(
                        target.period,
                        target.periodKey,
                      )} · newest first`
                    : ''}
                </Text>
              </View>
              <GlassButton variant="ghost" size="sm" onPress={onClose}>
                Close
              </GlassButton>
            </View>

            {error ? <Text style={styles.error}>{error}</Text> : null}

            {loading ? (
              <ActivityIndicator color={colors.accent} style={styles.loader} />
            ) : (
              <div
                style={{
                  maxHeight: listMaxHeight,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {rows.length === 0 ? (
                  <Text style={styles.empty}>
                    {usingSample
                      ? 'No sample transactions in this category.'
                      : 'No transactions in this period for this category.'}
                  </Text>
                ) : (
                  rows.map((tx) => {
                    const signed =
                      tx.direction === 'credit'
                        ? formatCurrency(tx.amount)
                        : `−${formatCurrency(tx.amount)}`;
                    const rowInner = (
                      <>
                        <View style={styles.rowMain}>
                          <View style={styles.merchantRow}>
                            <Text style={styles.merchant} numberOfLines={1}>
                              {tx.merchant}
                            </Text>
                            {tx.hiddenFromHousehold ? <PersonalOnlyChip /> : null}
                          </View>
                          {tx.ownerLabel ? (
                            <Text style={styles.owner} numberOfLines={1}>
                              {tx.ownerLabel}
                            </Text>
                          ) : null}
                          <Text style={styles.date}>{formatShortDate(tx.date)}</Text>
                          {tx.note && tx.note !== tx.merchant ? (
                            <Text style={styles.note} numberOfLines={1}>
                              {tx.note}
                            </Text>
                          ) : null}
                        </View>
                        <Text
                          style={[
                            styles.amount,
                            tx.direction === 'credit' && styles.amountCredit,
                          ]}
                        >
                          {signed}
                        </Text>
                        {tx.canEdit ? <TransactionEditGlyph /> : null}
                      </>
                    );
                    if (tx.canEdit) {
                      return (
                        <Pressable
                          key={tx.id}
                          style={styles.row}
                          onPress={() =>
                            setEditTarget({
                              id: tx.id,
                              merchant: tx.merchant,
                              categoryName: tx.categoryName ?? target?.categoryName,
                              hiddenFromHousehold: tx.hiddenFromHousehold,
                            })
                          }
                          accessibilityLabel={`Edit ${tx.merchant}`}
                        >
                          {rowInner}
                        </Pressable>
                      );
                    }
                    return (
                      <View key={tx.id} style={styles.row}>
                        {rowInner}
                      </View>
                    );
                  })
                )}
              </div>
            )}
          </GlassSurface>
        </View>
      </View>

      <EditTransactionModal
        visible={editTarget != null}
        initial={editTarget}
        saving={saving}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    zIndex: 1,
  },
  panel: {
    width: '100%',
    borderRadius: radii.xl,
  },
  panelContent: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  eyebrow: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  title: {
    ...typography.headline,
    color: colors.text,
    fontSize: 20,
  },
  sub: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  loader: {
    marginVertical: spacing.xl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  rowMain: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  merchantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minWidth: 0,
  },
  merchant: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    flexShrink: 1,
  },
  owner: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  date: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  note: {
    ...typography.caption,
    color: colors.textMuted,
  },
  amount: {
    ...typography.body,
    color: colors.warning,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  amountCredit: {
    color: colors.accent,
  },
  empty: {
    ...typography.body,
    color: colors.textMuted,
    paddingVertical: spacing.lg,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
  },
});
